/* eslint-disable max-lines */
// about moment
// Copyright (c) JS Foundation and other contributors
//
// Permission is hereby granted, free of charge, to any person
// obtaining a copy of this software and associated documentation
// files (the "Software"), to deal in the Software without
// restriction, including without limitation the rights to use,
// copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the
// Software is furnished to do so, subject to the following
// conditions:
//
// The above copyright notice and this permission notice shall be
// included in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES
// OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
// NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
// HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY,
// WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
// FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR
// OTHER DEALINGS IN THE SOFTWARE.

const moment = require('moment');
import config from '../config';

const MAX_ROMID_LENGTH = 10; // 5 byte
const MAX_CID_LENGTH = 32; // 16 byte
const hexStringPattern = /^[A-Fa-f0-9,]*$/;

const checkValidCharacters = (inputValue, rangeMin = 0x20, rangeMax = 0x7E) => {
  const bytesArray = inputValue.match(/.{1,2}/g);
  for (let byteIndex = 0; byteIndex < bytesArray.length; ++byteIndex) {
    const byteValue = parseInt(bytesArray[byteIndex], 16);
    if (byteValue < rangeMin || byteValue > rangeMax) {
      return false;
    }
  }
  return true;
};

// Convert milliseconds to mins and seconds
export const timeSpanConverter = (timeSpan) => {
  const result = { minutes: 0, seconds: 0, totalSeconds: 0 };
  if (timeSpan <= 0) {
    return result;
  }
  result.minutes = Math.round(timeSpan / 60000);
  result.seconds = Math.round((timeSpan % 60000) / 1000);
  result.totalSeconds = result.seconds;
  if (result.minutes > 0) {
    result.totalSeconds = (result.minutes * 60) + result.seconds;
  }
  return result;
};

// 時刻変換関数
export const timeConverter = (unixtime, locale = null, type = null) => { // eslint-disable-line no-unused-vars

  // 文字列が指定されていた場合にそのまま返す
  if (typeof (unixtime) === "string") {
    return unixtime;
  }

  if (!unixtime || unixtime === 0) {
    // 時間の変換ができない場合は空文字列を戻す
    return '';
  }
  let dateFormat = 'YYYY/MM/DD HH:mm:ss';
  if (type === 'day') {
    dateFormat = 'YYYY/MM/DD';
  } else if (type === 'min') {
    dateFormat = 'YYYY/MM/DD HH:mm';
  }
  // 言語情報からフォーマットを変換するか検討する。 
  const result = moment(unixtime).format(dateFormat);
  return result;
};

// date object を unix時刻に変換する。 
export const dateConverter = (dateObject) => {

  let result = 0;
  if (dateObject) {
    result = moment(dateObject).valueOf();
  }
  return result;
};

// ブラウザーの設定言語を取得 
export const getWindowNavigatorLanguage = (languageList) => {
  // 
  let language = 'en';
  try {
    const windowLanguage = window.navigator.language;
    languageList.forEach((lng) => {
      if (windowLanguage.indexOf(lng.value) !== -1) {
        language = lng.value;
      }
    });
  } catch (e) {
    language = 'en';
  }
  return language;
};

// UniqueIdを文字列で取得する関数(引数は省略可能) 
export const getUniqueIdStr = (myStrong = null) => {
  let strong = 1000;
  if (myStrong) strong = myStrong;
  return new Date().getTime().toString(16) + Math.floor(strong * Math.random()).toString(16);
};

export const getDaysOnLocale = () => {
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
};

// 改行コードを空白文字に変換する
export const deleteLineBreak = (fromLineBreakToEmpty) => {
  if (fromLineBreakToEmpty !== null) {
    const replaceLineBreak = fromLineBreakToEmpty.replace(/(\r\n|\n)/g, '');
    return replaceLineBreak;
  }
  return fromLineBreakToEmpty;
};

// 階層検索関数
export const deepSearch = (object, key, predicate, result) => {

  try {
    if (Object.prototype.hasOwnProperty.call(object, key) && predicate(key, object[key]) === true) {
      result.push(object);
      return object;
    }
    for (let i = 0; i < Object.keys(object).length; i++) {
      // 値が null か判定する
      if (object[Object.keys(object)[i]]) {
        if (typeof object[Object.keys(object)[i]] === "object") {
          deepSearch(
            object[Object.keys(object)[i]],
            key,
            predicate,
            result
          );
        }
      }
    }
    return result;
  } catch (e) {
    return null;
  }
};

// 配列の要素を連結して文字列に変換する
export const joinValue = (array) => {
  return (array && array.length !== 0) ? array.join(',') : '';
};

// グループ指定でエリアを抽出
export const getAreasByGroup = (group, groupAreaList, areaList) => {
  const ret = [];
  groupAreaList.forEach((groupArea) => {
    if (groupArea.group === group) {
      groupArea.areas.forEach((area) => {
        areaList.forEach((areaObj) => {
          if (areaObj.value === area) {
            ret.push(areaObj);
          }
        });
      });
    }
  });
  return ret;
};

// グループ化関数 
export const groupBy = (xs, key) => {
  return xs.reduce((rv, x) => {
    (rv[x[key]] = rv[x[key]] || []).push(x);
    return rv;
  }, {});
};

// 文字列の "true","false" を真偽値に変換
// 上記以外の文字列や、文字列以外の値が指定された場合は null を返す
export const strToBool = (str) => {
  let result = null;
  const val = str.toLowerCase().trim();
  if (val === 'true') result = true;
  else if (val === 'false') result = false;
  return result;
};

// ポーリング処理の結果情報作成関数
export const createResultInfo = (status, message = null, behaviorType = null, validatorResult = null, other = null, deviceSupported = true) => {
  const result =
  {
    status: status,
    error: {
      message: message,
      behaviorType: behaviorType,
      validatorResult: validatorResult,
      other: other,
      deviceSupported: deviceSupported
    }
  };
  return result;
};

/**
 * リソース ID を使用したパンくずデータを作成する。
 * @param {object} route Route
 * @param {object} navigationResources 画面とリソース ID のマッピング
 * @returns {object} パンくずデータ
 */
export const createBreadCrumbData = (route, navigationResources) => {
  const resourceId = navigationResources[route.name];
  return createBreadCrumbBaseData(route, resourceId);
};

/**
 * パンくずリスト用のデータを作成する。
 * @param {object} route Route
 * @param {string} resourcesId 表示するリソース ID
 * @param {string} displayText 表示するテキスト
 * @returns {object} パンくずデータ
 */
export const createBreadCrumbBaseData = (route, resourcesId, displayText) => {
  const data = {
    name: route?.name,
    path: route?.path,
    param: route?.params || null,
    resourcesId: resourcesId || null,
    displayText: displayText || null,
    level: route?.meta?.breadCrumbLevel,
    isHome: !!route?.meta?.isHome,
  };
  return data;
};

// 区切られた文字列を分解して返却する
// str : 文字列
// n : 何番目の文字か指定（０オリジン）
// delimiter : 区切り文字（省略可）
export const splitStr = (str, n, delimiter) => {
  const delm = delimiter || config.VEHICLEINFO_DELIMITER;
  if (str) {
    const arr = str.split(delm);
    if (arr[n]) {
      return arr[n].trim();
    }
  }
  return "";
};

// 文字列の改行コードをhtmlの改行コードに変換する
export const joinLineBreak = (value) => {
  let result = "";
  if (value) {
    result = value.split('\n').join('<br/>');
  }
  return result;
};

// last_evaluated_key 保持チェック
export const hasLastEvaluatedKey = (responseData) => {
  let result = false;
  if (responseData) {
    if (responseData.last_evaluated_key) {
      // 次のデータが存在しないか
      result = true;
    }
  }
  return result;
};

// Function to convert string to hex.
export const stringToHex = (stringValue) => {
  const hexNumberList = [];
  for (let index = 0; index < stringValue.length; ++index) {
    hexNumberList.push(Number(stringValue.charCodeAt(index)).toString(16));
  }
  return hexNumberList.join('').toUpperCase();
};

// Function to convert hex to string.
export const hexToString = (hexValue) => {
  const charList = [];
  for (let index = 0; index < hexValue.length; index += 2) {
    const code = parseInt(hexValue.substr(index, 2), 16);
    if (!isNaN(code) && code !== 0) {
      charList.push(String.fromCharCode(code));
    }
  }
  return charList.join('');
};

// Function to validate input value based on the ROMID/CID specification.
export const validateRomIdCid = (inputValue, isRomId) => {
  const inputValueLength = inputValue.length;
  if (inputValueLength === 0) {
    return false;
  }

  if (isRomId) {
    // Max length is 5 bytes.
    if (inputValueLength > MAX_ROMID_LENGTH) return false;

    // Valid values are hex.
    return hexStringPattern.test(inputValue);
  }

  // CID max length is 16 bytes.
  if (inputValueLength > MAX_CID_LENGTH) return false;

  // Valid values are 0x20 ~ 0x7E
  return checkValidCharacters(inputValue);
};

// Function to validate ssmid.
export const validateSSMId = (ssmId) => {
  if (ssmId.length < 2 || ssmId.length > 6 ||    // Length check
    ssmId.length % 2 > 0 ||                        // Even length check
    !hexStringPattern.test(ssmId)) {             // Hex value check
    return false;
  }
  // Validate each bytes for the ssm id (value range is 0x00 ~ 0xFF)
  return checkValidCharacters(ssmId, 0x00, 0xFF);
};

// Function to validate the character range is 0x20 to 0x7E
export const hasValidCharacters = (inputValue) => {
  if (inputValue.length === 0) {
    return false;
  }

  return checkValidCharacters(inputValue);
};

// zero padding
export const addPadding = (id, max) => {
  let ret = id;
  if (ret) {
    while (ret.length < max) { ret = '0' + ret; }
  }
  return ret;
};

// Function to remove padding from the input based on ROMID/CID/SWID.
export const removePadding = (inputValue, isRomId) => {
  const inputValueTemp = inputValue;
  let outputValue = inputValue;
  if (isRomId) {
    outputValue = inputValueTemp.replace(/^0+/, ''); // Remove leading 0's
  } else {
    outputValue = inputValueTemp.replace(/0+$/, ''); // Remove trailing 0's
    if (outputValue.length % 2 === 1) { // Add 0 if it is not even number length
      outputValue += '0';
    }
  }
  return outputValue;
};

// トリム処理
export const trimStr = (arg) => {
  return arg ? arg.trim() : '';
};

const isNullOrDefault = (value, defaultValue = null) => {
  let result = value;
  if (result === null || result === undefined) {
    result = defaultValue;
  }
  return result;
};

/**
 * キーでソートする
 * @param {Array} items ソート対象の配列
 * @param {string} sortKey ソートキー
 * @param {string} sortDir 昇順/降順を決める。デフォルトは降順
 * @param {boolean} numeric ソート時のみ数値で扱う場合のフラグ
 * @returns 
 */
export const sortItems = (items, sortKey, sortDir = config.DESC, numeric = false) => {
  let resultItems = items;
  if (items) {
    resultItems = items
      .sort((a, b) => {
        if (a[sortKey] !== a[sortKey] && b[sortKey] !== b[sortKey]) return 0;
        if (a[sortKey] !== a[sortKey]) return 1;
        if (b[sortKey] !== b[sortKey]) return -1;

        if (a[sortKey] === null && b[sortKey] === null) return 0;
        if (a[sortKey] === null) return 1;
        if (b[sortKey] === null) return -1;

        if (a[sortKey] === '' && b[sortKey] === '') return 0;
        if (a[sortKey] === '') return 1;
        if (b[sortKey] === '') return -1;

        const sig = sortDir === config.DESC ? 1 : -1;
        // ソート時だけ数値に変換する
        if (numeric) {
          const aNum = Number(a[sortKey]);
          const bNum = Number(b[sortKey]);
          return aNum < bNum ? sig : (aNum > bNum ? -sig : 0);
        }
        return a[sortKey] < b[sortKey] ? sig : (a[sortKey] > b[sortKey] ? -sig : 0);
      });
  }
  return resultItems;
};

// 前回指定された画面名を取得します。
// ※注意 : パラメータに画面名が設定された場合のみ取得可能です  
export const getPreviousDisplayName = (route) => {
  let name = "";
  if (route && route.params) {
    if (Object.prototype.hasOwnProperty.call(route.params, 'previousDisplayName')) {
      name = route.params.previousDisplayName;
    }
  }
  return name;
};

// 引数で渡されたTreeデータのSelected状態を解除します。 
export const relaseTreeSelected = (tree) => {
  const itemSelectedDatas = [];
  deepSearch(tree, 'isSelected', (_, val) => val === true, itemSelectedDatas);
  if (itemSelectedDatas && itemSelectedDatas.length > 0) {
    itemSelectedDatas[0].isSelected = false;
  }
  return tree;
};

// セッションタイムアウト時間を返します。
export const getSessionLimit = (loginResponse) => {
  const limit = loginResponse.data && loginResponse.data.session_timeout > 0 ? loginResponse.data.session_timeout : 30;
  return limit;
};

// 待機処理
// @params milliseconds 待機時間（ミリ秒）
export const sleep = async (msec) => {
  const delay = Number(msec);
  if (!isNaN(delay)) {
    return new Promise(resolve => setTimeout(resolve, msec));
  }
  return null;
};

// 選択されたファイルの先頭を返す
// @params data 選択されたファイル
export const selectFirstFile = (data) => {
  return data && data.length > 0 ? data[0] : null;
};

// SSM5アプリケーションの入力禁止チェック
export const checkSSM5ValidCharacters = (event, isLoginDisplay = false) => {
  if (event) {
    const keyCode = event.which || event.keyCode;
    // Backspace handling.
    const nodeName = event.target.nodeName.toLowerCase();
    if (keyCode === 8 && (nodeName !== 'input' && nodeName !== "textarea") || event.target.readOnly) {
      event.preventDefault();
      event.returnValue = false;
      return false;
    }
    // Unsupported key handling.
    // Login画面からの入力チャックの場合、「"」文字の入力は許容する
    if (!isLoginDisplay) {
      if ((event.shiftKey && keyCode === 50) || (event.shiftKey && keyCode === 222)) {
        if (event.key === '"') {
          return false;
        }
      }
    }
    if ((!event.shiftKey && keyCode === 226) || (!event.shiftKey && keyCode === 220)) {
      return false;
    }
  }
  return true;
};

// SSM5アプリケーションのPasteチェック 
export const checkSSM5ValidPaste = (event) => {
  if (event) {
    const data = event.clipboardData.getData('Text');
    if (data.indexOf('"') >= 0 || data.indexOf('\\') >= 0) {
      return false;
    }
  }
  return true;
};

/* eslint-disable max-lines */
export const getType2DisplayUnitId = (id, masterUnits) => {
  if (!id || masterUnits.length < 1) return null;
  // SU法規対象 Type2 の表示システム名を設定
  const system = masterUnits.filter(unit => unit.id === id.toString());
  if (system.length > 0) {
    if (system[0].name === 'RCR_L' || system[0].name === 'RCR_R' || system[0].name === 'RCR_R_25ePF' || system[0].name === 'RCR_L_25ePF') {
      const rcr = masterUnits.filter(ecu => ecu.name === 'RCR');
      return rcr.length > 0 ? rcr[0].id : null;
    } else if (system[0].name === 'FCR_L' || system[0].name === 'FCR_R' || system[0].name === 'FCR_R_25ePF' || system[0].name === 'FCR_L_25ePF') {
      const fcr = masterUnits.filter(ecu => ecu.name === 'FCR');
      return fcr.length > 0 ? fcr[0].id : null;
    } else if (system[0].name === 'EPS_M' || system[0].name === 'EPS_S') {
      const eps = masterUnits.filter(ecu => ecu.name === 'EPS');
      return eps.length > 0 ? eps[0].id : null;
    }
  }
  return null;
};

export const setColums = (first, second, columFormat, isSecondRower = false) => {
  if (isSecondRower) {
    const fontNormal = '<span style="font-weight:normal;">' + second + '</span>';
    const text = columFormat.format(first, fontNormal);
    return text;
  }
  else {
    const text = columFormat.format(first, second);
    return text;
  }
};

export const isKeyExists = (obj, key) => {
  return key in obj;
};

export const sliceArray = (array, num) => {
  const len = Math.ceil(array.length / num);
  return new Array(len).fill().map((_, i) => array.slice(i * num, (i + 1) * num));
};

export const isSupportBrowser = () => {
  let result = true;
  try {
    const userAgent = window.navigator.userAgent.toLowerCase();
    if (userAgent.indexOf('msie') !== -1 || userAgent.indexOf('trident') !== -1) {
      //IE判定
      result = false;
      // } else if (userAgent.indexOf('edge') != -1) {
      //Edge(Legacy)判定
      // result = false;
      // } else if (userAgent.indexOf('chrome') != -1) {
      //   //Google Chrome向けの記述
      // } else if (userAgent.indexOf('safari') != -1) {
      //   //Safari向けの記述
      // } else if (userAgent.indexOf('firefox') != -1) {
      //   //FireFox向けの記述
    } else {
      //その他のブラウザ向けの記述
      result = true;
    }
  } catch (e) {
    result = true;
  }
  return result;
};

export const mkHeaderValue = (arg) => {
  return arg || '-';
};

export const hasArrayList = (arg) => {
  return arg !== null && arg.data.length > 0 ? true : false;
};

// 秒に変換する
export const convertToSec = (arg, type) => {
  if (type === 'min') {
    const min = Math.floor(arg * 60);
    return min;
  }
  return arg;
};

export const getImbMenuKeyFromId = (menuId) => {
  if (menuId === 'MENU_REGISTER_IMB_SYSTEM') {
    return 'imb.menu_register_imb_system';
  } else if (menuId === 'MENU_REGISTER_ECU') {
    return 'imb.menu_register_ecu';
  } else if (menuId === 'MENU_REGISTER_CGW') {
    return 'imb.menu_register_cgw';
  } else if (menuId === 'MENU_REGISTER_COMBINATION_METER') {
    return 'imb.menu_register_combination_meter';
  } else if (menuId === 'MENU_DELETE_IMB_KEY_ID') {
    return 'imb.menu_delete_imb_key_id';
  } else if (menuId === 'MENU_READ_NUM_REGISTERED_KEYS') {
    return 'imb.menu_read_num_registered_keys';
  } else if (menuId === 'MENU_REGISTER_REMOTE_ENGINE_START_CU') {
    return 'imb.menu_register_remote_engine_start_cu';
  } else if (menuId === 'MENU_REGISTER_TELEMATICS_CU') {
    return 'imb.menu_register_telematics_cu';
  } else if (menuId === 'MENU_REGISTER_KEYLESS_ACCESS_IMB') {
    return 'imb.menu_register_keyless_access_imb';
  } else if (menuId === 'MENU_REGISTER_STEERING_LOCK') {
    return 'imb.menu_register_steering_lock';
  } else if (menuId === 'MENU_READ_NUM_REGISTERED_ACCESS_KEYS') {
    return 'imb.menu_read_num_registered_access_keys';
  } else if (menuId === 'MENU_DELETE_ACCESS_KEY_ID') {
    return 'imb.menu_delete_access_key_id';
  } else if (menuId === 'MENU_READ_REGISTRATION_HISTORY') {
    return 'imb.menu_read_registration_history';
  } else if (menuId === 'MENU_REGISTER_HVECU') {
    return 'imb.menu_register_hv_ecu';
  }
  return "";
};

/**
 * 遷移元画面の判定
 * @param {string} fromName 遷移元画面名
 * @returns 単位設定またはパスワード変更画面の場合、true を返す
 */
export const isFromSettingsPage = (fromName) => {
  // 単位設定またはパスワード変更の場合、true を返す
  return fromName === 'changeUnit' || fromName === 'changePassword';
};

/**
 * 遷移元画面のシステムが同じか判定
 * @param {*} from 遷移元画面パラメータ
 * @returns 遷移元画面のシステムが同じ場合、true を返す
 */
export const isSameSystemPage = (from) => {
  // 遷移元画面のシステムが同じ場合、true を返す
  return !!(from.query && parseBoolean(from.query.sameSystemFlg));
};

/**
 * CSV出力用にエスケープ処理を施す
 * @param {String} orgVal 処理前の値
 * @returns 処理後の値
 */
export const escapeForCsv = (orgVal) => {
  // eslint-disable-next-line no-useless-escape
  return `"${orgVal.replace(/\"/g, '\"\"')}"`;
};

/**
 * CSVエクスポート処理
 * @param {String} data CSVデータ
 * @param {String} fileName ファイル名
 */
export const csvFileExport = (data, fileName) => {
  const bomFormatData = new Uint8Array([0xEF, 0xBB, 0xBF]); // UTF-8 BOM
  const blob = new Blob([bomFormatData, data], { type: 'text/csv;charset=utf-8' });

  const link = window.document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const createCsvData = (headers, records) => {
  const csvLines = [];
  const csvHeaders = headers.map((header) => `"${header.title}"`).join(',');

  records.forEach((record) => {
    const arr = [];
    headers.forEach((header) => {
      if (record[header.id] || record[header.id] === 0) {
        arr.push(`"${record[header.id]}"`);
      } else {
        arr.push(record[header.id]);
      }
    });
    csvLines.push(arr.join(',') + '\r\n');
  });
  const data = csvHeaders + '\r\n' + csvLines.join('');
  return data;
};
export function createQueryParams(params) {
  return Object.keys(params)
    .filter((k) => params[k] !== null && params[k] !== undefined)
    .map(
      (k) =>
        encodeURIComponent(k) + '=' + encodeURIComponent(params[k]),
    )
    .join('&');
}

// Function to check resion is china.
export function isChinaResion() {
  if (process.env.VUE_APP_BUILD_ENVIRONMENT === 'cn') {
    return true;
  }
  return false;
}

// 指定された値をBooleanに変換する 
export function parseBoolean(value) {
  switch (value) {
    case true:
    case 'true':
    case 1:
    case '1':
      return true;
    case false:
    case 'false':
    case '':
    case 0:
    case -1:
    case '0':
    case '-1':
    case null:
    case undefined:
      return false;
    default:
      return value;
  }
}

export function hasPropertyCheck(obj, key) {
  // hasOwnPropertyで直下には見つからなくても直接値を指定すると引きあたる場合があるので2重チェックを実行
  let propertyCheck = false;
  if (obj) {
    propertyCheck = Object.prototype.hasOwnProperty.call(obj, key);
    if (!propertyCheck) {
      if (obj[key] !== undefined) {
        return true;
      }
    }
  }
  return propertyCheck;
}


/**
 * ダブルクォーテーションのエスケープ処理
 * @param {*} records 出力対象
 * @param {*} columnName 対象カラム名
 * @returns エスケープ処理後のレコード
 */
export const escapeDoubleQuotation = (records, columnName) => {
  // 文字列中の全ての " を "" に変換
  return records.map((info) => {
    if (info[columnName]) {
      info[columnName] = info[columnName].replace(/"/g, '""');
    }
    return info;
  });
};

/**
 * 2つの引数の大小を比較する。
 * @param {object} a 1つ目
 * @param {object} b 2つ目
 * @param {string} sortDir 昇順か降順か（デフォルトは降順）
 * @returns {int} 昇順の場合は 1 つ目の方が大きい場合に 1、降順の場合は 2 つ目の方が大きい場合に 1 を返す。同じ場合は 0を返す。
 */
export const compare = (a, b, sortDir = config.DESC) => {
  const sig = sortDir === config.DESC ? 1 : -1;
  return a < b ? sig : (a > b ? -sig : 0);
};

/**
  * @typedef {object} config DID表定義
  * @property {string} signature  signed or unsigned
  * @property {string} byte_or_bit  byte or bit
  * @property {num} byte_size  byte_or_bit is byte only
  * @property {num} bit_size  byte_or_bit is bit only
  * @property {num} scaling_bit  scaling_bit
  * @property {num} offset  offset
  */

/**
 * DIDの値である16進数文字列を符号付き数値に変換する
 * @param {config} config 16進数文字列を数値に変換するのに必要なDID表定義
 * @param {string} value 16進数文字列
 * @returns {number|string} 変換結果(小数点の桁数調整が発生した場合のみ、文字列で返却される)
 */
export const convertHexOfDidToInt = (config, value) => {
  let valueTemp = parseInt(value, 16);
  if (config.signature === 'signed') {
    const bitSize = config.byte_or_bit === 'bit' ? config.bit_size : config.byte_size * 8;

    // 符号なし整数の最大値
    const maxUnsignedValue = Math.pow(2, bitSize) - 1;
    // 符号付き整数の最大値
    const maxSignedValue = Math.pow(2, bitSize - 1) - 1;

    // 符号付き整数の最大値を超える場合
    if (valueTemp > maxSignedValue) {
      valueTemp = valueTemp - (maxUnsignedValue + 1);
    }
  }
  valueTemp = (valueTemp * config.scaling_bit) + config.offset;
  // 小数点の桁数調整
  const dec = config.digits_after_decimal;
  if (dec !== null && dec !== undefined) {
    valueTemp = valueTemp.toFixed(dec);
  }
  return valueTemp;
};

/**
 * HTMLエスケープ処理を施す
 * @param {String} orgVal 処理前の値
 * @returns 処理後の値
 */
export const escapeHTML = (orgVal) => {
  const escapeMap = {
    '&': '&amp;',
    '"': '&quot;',
    "'": '&#39;',
    '<': '&lt;',
    '>': '&gt;'
  };
  return orgVal.replace(/[&"'<>]/g, (match) => escapeMap[match]);
};

/**
 * GMTからLocalTimeへ変換
 * @param {*} responseDate 絶対時間タイプスタンプ情報 
 */

export const convertLocalDateTime = (responseDate) => {
  if (!responseDate) {
    return null;
  }

  const globalDate = new Date(responseDate);
  // 日付として無効な文字列の場合Backendから帰ってきた文字列をそのまま表示する
  if (globalDate.toString() === 'Invalid Date') {
    return responseDate;
  }

  // LocalTimeに変換
  return timeConverter(globalDate);
};


// STG環境クラウドフロントURLでaccess判定
export const isCloudFrontUrl = () => {
  return window.location.href.includes("cloudfront.net");
};

/**
 * pack.jsonを取得するリージョンを返す
 * @param {String} dataSource - データソース指定フラグ（未指定の場合は表示サイトの環境判定を使用）
 * @returns {string} リージョン
 */
export const getPackJsonRegion = (dataSource = null) => {
  // データソース指定がない場合は、表示サイトのリージョンを返す
  if (!dataSource) {
    const isMirror = window.location.href.includes("mirror");
    return isMirror ? process.env.VUE_APP_MIRROR_REGION : process.env.VUE_APP_MASTER_REGION;
  }
  // データソース指定がある場合は、指定されたデータソースに応じたリージョンを返す
  return dataSource === 'mirror' ? process.env.VUE_APP_MIRROR_REGION : process.env.VUE_APP_MASTER_REGION;
};

// HACK: 本ファイルは肥大化する一方であるため、xxxUtils.jsのように用途や目的別のファイルを作成して分割すること。

export default {
  timeSpanConverter,
  dateConverter,
  timeConverter,
  getDaysOnLocale,
  deepSearch,
  joinValue,
  getAreasByGroup,
  groupBy,
  getWindowNavigatorLanguage,
  strToBool,
  createResultInfo,
  createBreadCrumbData,
  createBreadCrumbBaseData,
  stringToHex,
  hexToString,
  validateRomIdCid,
  hasValidCharacters,
  addPadding,
  removePadding,
  getUniqueIdStr,
  trimStr,
  splitStr,
  hasLastEvaluatedKey,
  sortItems,
  joinLineBreak,
  getPreviousDisplayName,
  relaseTreeSelected,
  getSessionLimit,
  sleep,
  selectFirstFile,
  checkSSM5ValidCharacters,
  checkSSM5ValidPaste,
  deleteLineBreak,
  getType2DisplayUnitId,
  isNullOrDefault,
  setColums,
  isKeyExists,
  sliceArray,
  isSupportBrowser,
  mkHeaderValue,
  hasArrayList,
  convertToSec,
  getImbMenuKeyFromId,
  isFromSettingsPage,
  isSameSystemPage,
  escapeForCsv,
  csvFileExport,
  createCsvData,
  createQueryParams,
  isChinaResion,
  parseBoolean,
  hasPropertyCheck,
  escapeDoubleQuotation,
  convertHexOfDidToInt,
  escapeHTML,
  convertLocalDateTime,
  isCloudFrontUrl,
  getPackJsonRegion
};